import React, { useEffect, useMemo, useReducer, useState } from 'react';
import TextInput from '../Common/TextInput';
import SelectInput from '../Common/SelectInput';
import CallOut from '../Common/CallOut';
import Notification from '../Common/Notification';
import ActivityLoader from '../Common/ActivityLoader';
import { useBanking } from '../../DataStore/BankingContext';
import { useAccounts } from '../../DataStore/AccountsContext';
import ajaxBanking from '../../util/remote/ajaxBanking';
import { useRefreshTrans } from '../../DataStore/useRefreshTrans';

const reducer = (state, action) => {
  if (action.type === 'RESET') {
    return { ...initialState };
  }
  return { ...state, [action.type]: action.value };
};

const initialState = {
  loading: false,
  bankAccount: null,
  ledgerAccount: null,
  direction: '',
  paymentType: '',
  transactionDate: '',
  amount: '',
  reference: '',
  description: '',
};

const directionOptions = [
  { value: 'Incoming', label: 'Incoming (Deposit)' },
  { value: 'Outgoing', label: 'Outgoing (Withdrawal)' },
];

const paymentTypeOptions = [
  { value: 'Cheque', label: 'Cheque' },
  { value: 'EFT', label: 'EFT' },
  { value: 'RTGS', label: 'RTGS' },
  { value: 'Wire', label: 'Wire' },
  { value: 'MobileMoney', label: 'Mobile Money' },
  { value: 'Other', label: 'Other' },
];

const NewBankDeposit = () => {
  const { bankListing, getBanks, getCheckingTransactions } = useBanking();
  const { accountsCatalogue, getAllAccounts } = useAccounts();
  const refreshBalances = useRefreshTrans();

  const [state, dispatch] = useReducer(reducer, initialState);
  const [currentNotify, setNotify] = useState(null);

  useEffect(() => {
    getBanks();
    getAllAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentNotify) {
      return;
    }
    const timeout = setTimeout(() => setNotify(null), 5000);
    return () => clearTimeout(timeout);
  }, [currentNotify]);

  const bankOptions = useMemo(() => {
    if (!Array.isArray(bankListing)) return [];
    return bankListing.map((bank) => ({
      label: `${bank.account_number || '—'} ${bank.bank_name}`,
      value: bank.bank_id || bank.account_id,
      raw: bank,
    }));
  }, [bankListing]);

  const ledgerOptions = useMemo(() => {
    if (!Array.isArray(accountsCatalogue)) return [];
    return accountsCatalogue.map((account) => ({
      label: `${account.account_number} ${account.account_name} (${account.account_type})`,
      value: account.account_id,
    }));
  }, [accountsCatalogue]);

  const requireField = (condition, message) => {
    if (condition) {
      setNotify(<Notification type="danger" message={message} />);
      return true;
    }
    return false;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (state.loading) return;

    const numericAmount = parseFloat(state.amount);
    if (requireField(Number.isNaN(numericAmount) || numericAmount <= 0, 'Enter a valid amount.')) {
      return;
    }
    if (requireField(!state.bankAccount?.value, 'Select the checking account that will be affected.')) {
      return;
    }
    if (requireField(!state.ledgerAccount?.value, 'Select the offset ledger account.')) {
      return;
    }
    if (requireField(!state.direction, 'Specify whether this is an incoming or outgoing entry.')) {
      return;
    }
    if (requireField(!state.paymentType, 'Select the payment channel used.')) {
      return;
    }
    if (requireField(!state.transactionDate, 'Choose the transaction/cheque date.')) {
      return;
    }

    const selectedBank = state.bankAccount?.raw || {};
    const payload = {
      bank_id: selectedBank.bank_id || state.bankAccount.value,
      cash_account: selectedBank.account_id || selectedBank.cash_account || state.bankAccount.value,
      ledger_account: state.ledgerAccount.value,
      direction: state.direction,
      payment_type: state.paymentType,
      cheque_date: state.transactionDate,
      reference: state.reference,
      narration: state.description,
      amount: numericAmount,
    };

    dispatch({ type: 'loading', value: true });
    const serverResponse = await ajaxBanking.createCheckingEntry(payload);
    dispatch({ type: 'loading', value: false });

    if (serverResponse?.status === 'OK') {
      dispatch({ type: 'RESET' });
      setNotify(<Notification type="success" message={serverResponse.message} />);
      if (typeof getCheckingTransactions === 'function') {
        await getCheckingTransactions(true);
      }
      if (typeof refreshBalances === 'function') {
        refreshBalances();
      }
    } else {
      setNotify(
        <Notification
          type="danger"
          message={serverResponse?.message || 'Something went wrong while saving the entry.'}
        />
      );
    }
  };

  return (
    <div className="card-body">
      <CallOut
        title="Checking Entries"
        description="Record deposits or withdrawals against a checking/bank account and keep the ledger up to date."
      />

      {currentNotify}

      <form className="form-horizontal" onSubmit={handleSave}>
        <SelectInput
          label="Checking Account"
          placeholder={state.bankAccount?.label || 'Select the bank account'}
          iconClass="fa fa-university"
          options={bankOptions}
          onChange={(item) => dispatch({ type: 'bankAccount', value: item })}
          leftComponent={<i className="fa fa-sync link" onClick={() => getBanks(true)}></i>}
          loading={!Array.isArray(bankListing)}
        />

        <SelectInput
          label="Ledger / Offset Account"
          placeholder={state.ledgerAccount?.label || 'Select the ledger account'}
          iconClass="fa fa-book"
          options={ledgerOptions}
          onChange={(item) => dispatch({ type: 'ledgerAccount', value: item })}
          leftComponent={<i className="fa fa-sync link" onClick={() => getAllAccounts(true)}></i>}
          loading={!Array.isArray(accountsCatalogue)}
        />

        <SelectInput
          label="Direction"
          placeholder={state.direction || 'Incoming or outgoing?'}
          iconClass="fa fa-random"
          options={directionOptions}
          onChange={(item) => dispatch({ type: 'direction', value: item?.value || '' })}
        />

        <SelectInput
          label="Payment Type"
          placeholder={state.paymentType || 'Select payment channel'}
          iconClass="fa fa-credit-card"
          options={paymentTypeOptions}
          onChange={(item) => dispatch({ type: 'paymentType', value: item?.value || '' })}
        />

        <TextInput
          type="date"
          label="Transaction Date"
          placeholder="Date entry was initiated"
          leftIcon="fa fa-calendar-alt"
          value={state.transactionDate}
          onChange={(e) => dispatch({ type: 'transactionDate', value: e.target.value })}
          required
          wrap
        />

        <TextInput
          type="number"
          label="Amount"
          placeholder="Enter amount"
          leftIcon="fa fa-money-bill"
          value={state.amount}
          onChange={(e) => dispatch({ type: 'amount', value: e.target.value })}
          required
          wrap
        />

        <TextInput
          type="text"
          label="Reference / Cheque No."
          placeholder="Optional reference"
          leftIcon="fa fa-hashtag"
          value={state.reference}
          onChange={(e) => dispatch({ type: 'reference', value: e.target.value })}
          wrap
        />

        <TextInput
          type="textarea"
          label="Memo / Description"
          placeholder="What is this entry for?"
          leftIcon="fa fa-align-left"
          value={state.description}
          onChange={(e) => dispatch({ type: 'description', value: e.target.value })}
          wrap
        />

        <div className="text-right">
          {!state.loading && <button className="btn btn-lg btn-success">Save Entry</button>}
          {state.loading && <ActivityLoader />}
        </div>
      </form>
    </div>
  );
};

export default NewBankDeposit;
